import React, { useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Modal, ModalTitle, ModalBody } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { InteractiveList } from '../../../InteractiveList/InteractiveList';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Input } from '../../../Input';
import { ModalFormRow, ModalAddButtonContainer, ModalFieldContainer } from '../../styles';
import { formatPhoneNumberIncrementally, phoneNumberUnformatted } from '../../../../utils';
import { formatPhoneNumber } from 'shared-utils/utils/formatPhoneNumber';
import { PHONE_VALIDATION_REGEX } from '../../../../constants';
import { client, ErrorResponse } from '../../../../api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';

interface AddBatchPhoneNumbersModalProps {
  open: boolean;
  onClose: () => void;
  existingNumbers: string[];
  isLoading: boolean;
}

interface FormData {
  phoneNumber: string;
}

const AddPhoneNumberForm = ({ existingNumbers }: { existingNumbers: string[] }) => {
  const queryClient = useQueryClient();

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      phoneNumber: '',
    },
  });

  const addPhoneNumber = useMutation<unknown, AxiosError<ErrorResponse>, string>({
    mutationFn: async (phoneNumber) => {
      return client.patch('settings/phone-numbers', { phone_numbers: [phoneNumber] }).then((res) => res.data);
    },
    onSuccess: () => {
      toast.custom(() => <Toast variant="success">Phone number added</Toast>);
      queryClient.invalidateQueries({ queryKey: ['office-phone-numbers'] });
      reset();
    },
    onError: (error) => {
      const message = error.response?.data?.error || 'Error adding phone number';
      toast.custom(() => <Toast variant="error">{message}</Toast>);
    },
  });
  const watchedValues = watch();
  const hasInput = watchedValues.phoneNumber?.trim();

  const onSubmit = (data: FormData) => {
    const cleanedPhoneNumber = phoneNumberUnformatted(data.phoneNumber.trim());

    if (existingNumbers.includes(cleanedPhoneNumber)) {
      setError('phoneNumber', { message: 'This phone number has already been added' });
      return;
    }
    addPhoneNumber.mutate(cleanedPhoneNumber);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalFormRow>
        <ModalFieldContainer>
          <Controller
            control={control}
            name="phoneNumber"
            rules={{
              required: { value: true, message: 'Phone number is required' },
              pattern: {
                value: PHONE_VALIDATION_REGEX,
                message: 'Please enter a valid phone number',
              },
            }}
            render={({ field }) => (
              <Input
                type="tel"
                label="Phone"
                placeholder="e.g. (555) 123-4567"
                hasError={!!errors.phoneNumber}
                disabled={addPhoneNumber.isPending}
                {...field}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatPhoneNumberIncrementally(e.target.value);
                  field.onChange(formatted);
                }}
              />
            )}
          />
          {errors.phoneNumber && <ErrorMessage>{errors.phoneNumber.message}</ErrorMessage>}
        </ModalFieldContainer>

        <ModalAddButtonContainer>
          <Button type="submit" variant="primary" disabled={!hasInput} isLoading={addPhoneNumber.isPending}>
            Add
          </Button>
        </ModalAddButtonContainer>
      </ModalFormRow>
    </form>
  );
};

/**
 * Modal for managing office phone numbers (add multiple and remove).
 */
const AddBatchPhoneNumbersModal: React.FC<AddBatchPhoneNumbersModalProps> = ({
  open,
  onClose,
  existingNumbers,
  isLoading,
}) => {
  const queryClient = useQueryClient();

  const removePhoneNumber = useMutation<unknown, AxiosError<ErrorResponse>, string>({
    mutationFn: async (phoneNumber) => {
      return client.delete(`settings/phone-numbers/${phoneNumber}`).then((res) => res.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['office-phone-numbers'] });
      toast.custom(() => <Toast variant="success">Phone number removed</Toast>);
    },
    onError: () => {
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const removingNumber = removePhoneNumber.isPending ? removePhoneNumber.variables : null;

  const displayItems = useMemo(
    () =>
      existingNumbers.map((number) => ({
        id: number,
        content:
          removingNumber === number ? (
            <div className="flex items-center gap-2">
              <Typography color="var(--grayscale-500)">{formatPhoneNumber(number)}</Typography>
              <LoadingSpinner height="auto" size="14px" />
            </div>
          ) : (
            <Typography>{formatPhoneNumber(number)}</Typography>
          ),
        isInteractionDisabled: removingNumber === number,
      })),
    [existingNumbers, removingNumber],
  );

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>Add office phone numbers</ModalTitle>
      <ModalBody>
        <AddPhoneNumberForm existingNumbers={existingNumbers} />
        {isLoading ? (
          <LoadingSpinner height="200px" />
        ) : displayItems.length > 0 ? (
          <InteractiveList items={displayItems} onRemoveItem={(id) => removePhoneNumber.mutate(id)} />
        ) : (
          <div className="flex min-h-16 items-center justify-center self-stretch rounded-medium border border-solid border-grayscale-200 bg-white px-3">
            <Typography styledAs="bodySmallDMSans" color="var(--grayscale-500)">
              No phone numbers have been added
            </Typography>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default AddBatchPhoneNumbersModal;
